import React, { useContext, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import CustomInput from '~/components/CustomInput'
import Dialog from '~/components/Dialog'
import { Nullable } from '~/types'
import { FeedPageContext } from '../..'
import notify from '~/components/Notify'

interface Props {
  barleyData: Nullable<{ index: number, key: string, feedName: string }>
  handleClose: () => void
}

const BarleyModal: React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const { getValues, setValue } = useFormContext()
  const { setEnergyCalcData } = useContext(FeedPageContext)

  const { barleyData, handleClose } = props
  const [barleyValues, setBarleyValues] = useState({
    dryMatter: '',
    energy: '',
    rawAsh: '',
    rawProtein: '',
    rawFat: '',
    rawFiber: ''
  })

  useEffect(() => {
    if (barleyData) {
      const barleyValue = getValues(`${barleyData?.key}[${barleyData.index}]`)
      setBarleyValues({
        dryMatter: barleyValue?.dryMatter || '',
        energy: barleyValue?.energy || '',
        rawAsh: barleyValue?.rawAsh || '',
        rawProtein: barleyValue?.rawProtein || '',
        rawFat: barleyValue?.rawFat || '',
        rawFiber: barleyValue?.rawFiber || ''
      })
    }
  }, [barleyData])

  function handleSubmit() {
    const barleyValue = getValues(`${barleyData?.key}[${barleyData.index}]`)

    setValue(`${barleyData?.key}[${barleyData.index}]`, { ...barleyValue, ...barleyValue })
    setBarleyValues({
      dryMatter: '',
      energy: '',
      rawAsh: '',
      rawProtein: '',
      rawFat: '',
      rawFiber: ''
    })
    notify(t('The content values for {{feedName}} have been successfully adjusted.', { feedName: barleyData?.feedName || '' }), 'success')
    setEnergyCalcData(null)
    handleClose()
  }

  function renderBarleyValues() {
    return <div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 20
      }}>
        <CustomInput
          value={barleyValues?.dryMatter}
          label={t('TM')}
          placeholder={t('TM')}
          decimalScale={2}
          suffix={' g/kg TM'}
          withLabels
          allowDecimals={true}
          onChange={(value) => setBarleyValues({ ...barleyValues, dryMatter: value || '' })}
        />
        <CustomInput
          value={barleyValues?.energy}
          label={t('Energy')}
          decimalScale={2}
          withLabels
          suffix={' NJ NEL/kg TM'}
          allowDecimals={true}
          placeholder={t('Energy')}
          onChange={(value) => setBarleyValues({ ...barleyValues, energy: value || '' })}
        />
        <CustomInput
          value={barleyValues?.rawAsh}
          label={'Rohasche'}
          decimalScale={2}
          withLabels
          suffix={' g/kg TM'}
          allowDecimals={true}
          placeholder={'in g/kg TM'}
          onChange={(value) => setBarleyValues({ ...barleyValues, rawAsh: value || '' })}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
        <CustomInput
          value={barleyValues?.rawProtein}
          label={'Rohprotein'}
          placeholder={'in g/kg TM'}
          decimalScale={2}
          suffix={' g/kg TM'}
          withLabels
          allowDecimals={true}
          onChange={(value) => setBarleyValues({ ...barleyValues, rawProtein: value || '' })}
        />
        <CustomInput
          value={barleyValues?.rawFat}
          label={'Rohfett'}
          decimalScale={2}
          withLabels
          suffix={' g/kg TM'}
          allowDecimals={true}
          placeholder={'in g/kg TM'}
          onChange={(value) => setBarleyValues({ ...barleyValues, rawFat: value || '' })}
        />
        <CustomInput
          value={barleyValues?.rawFiber}
          label={'Rohfaser'}
          decimalScale={2}
          withLabels
          suffix={' g/kg TM'}
          allowDecimals={true}
          placeholder={'in g/kg TM'}
          onChange={(value) => setBarleyValues({ ...barleyValues, rawFiber: value || '' })}
        />
      </div>
    </div>
  }

  return <Dialog
    textHeader={t('Edit barley for {{feedName}}', { feedName: barleyData?.feedName || '' })}
    textClose={t('Cancel')}
    type='abort'
    textSubmit={t('Adjust')}
    disableSubmit={Object.values(barleyValues).findIndex(barleyValue => barleyValue === '') >= 0}
    isDialogOpen={Boolean(barleyData)}
    handleClose={() => handleClose()}
    handleSubmit={() => handleSubmit()}
    textContent={renderBarleyValues()}
  />
}
export default BarleyModal